import { z } from 'zod';
import {
  BUSINESS_KINDS,
  type BusinessKind,
  DUPLICATE_RESOLUTIONS,
  type DuplicateResolution,
  IMPORT_CONSENT_BASES,
  IMPORT_ROW_STATUSES,
  type ImportConsentBasis,
  type ImportRowStatus,
  type ImportStatus,
} from '../constants';

/** Largest import accepted: rows after the header, and bytes of the uploaded file (sent as multipart field `file`). */
export const MAX_IMPORT_ROWS = 10_000;
export const MAX_IMPORT_BYTES = 25 * 1024 * 1024;

/** Formats the importer reads (detected from the file's content first, the extension only as a hint). */
export const IMPORT_FILE_TYPES = ['csv', 'tsv', 'txt', 'xlsx', 'xls', 'ods', 'json', 'xml', 'vcf', 'html', 'pdf', 'docx'] as const;
export type ImportFileType = (typeof IMPORT_FILE_TYPES)[number];

export const IMPORT_FILE_TYPE_LABELS: Record<ImportFileType, string> = {
  csv: 'CSV',
  tsv: 'Tab-separated text',
  txt: 'Plain text',
  xlsx: 'Excel workbook',
  xls: 'Excel 97–2003 workbook',
  ods: 'OpenDocument spreadsheet',
  json: 'JSON',
  xml: 'XML',
  vcf: 'Contacts (vCard)',
  html: 'Web page',
  pdf: 'PDF',
  docx: 'Word document',
};

/** File-picker hint only: the server reads the content and decides, so other files may still be dropped. */
export const IMPORT_FILE_EXTENSIONS = ['.csv', '.tsv', '.txt', '.xlsx', '.xlsm', '.xls', '.ods', '.json', '.xml', '.vcf', '.html', '.htm', '.pdf', '.docx'] as const;

/** CRM fields a CSV column can map to. Contact fields create the lead's first contact. */
export const IMPORT_FIELD_KEYS = [
  'name',
  'category',
  'restaurantType',
  'addressLine1',
  'addressLine2',
  'city',
  'region',
  'postalCode',
  'country',
  'phone',
  'website',
  'email',
  'pizzaFocus',
  'ownershipType',
  'locationCount',
  'openedOn',
  'description',
  'ownerEmail',
  'tags',
  'contactFullName',
  'contactFirstName',
  'contactLastName',
  'contactTitle',
  'contactEmail',
  'contactPhone',
] as const;
export type ImportFieldKey = (typeof IMPORT_FIELD_KEYS)[number];

export type ImportFieldInfo = {
  label: string;
  group: 'business' | 'contact' | 'assignment';
  /** Lower-case header spellings (EN/FR) recognised automatically. */
  synonyms: readonly string[];
  hint?: string;
};

export const IMPORT_FIELDS: Record<ImportFieldKey, ImportFieldInfo> = {
  name: { label: 'Business name', group: 'business', synonyms: ['name', 'business name', 'business', 'restaurant', 'restaurant name', 'company', 'company name', 'nom', 'nom du restaurant', 'entreprise', 'pizzeria'] },
  category: { label: 'Category', group: 'business', synonyms: ['category', 'catégorie', 'categorie', 'type of business'] },
  restaurantType: { label: 'Restaurant type', group: 'business', synonyms: ['restaurant type', 'type', 'style', 'cuisine', 'type de restaurant'] },
  addressLine1: { label: 'Street address', group: 'business', synonyms: ['address', 'street', 'street address', 'address 1', 'address line 1', 'adresse', 'rue'] },
  addressLine2: { label: 'Address line 2', group: 'business', synonyms: ['address 2', 'address line 2', 'suite', 'unit', 'local'] },
  city: { label: 'City', group: 'business', synonyms: ['city', 'town', 'ville', 'municipality'] },
  region: { label: 'Province / state', group: 'business', synonyms: ['province', 'state', 'region', 'prov', 'état', 'etat', 'région'] },
  postalCode: { label: 'Postal / ZIP code', group: 'business', synonyms: ['postal code', 'postal', 'zip', 'zip code', 'postcode', 'code postal'] },
  country: { label: 'Country', group: 'business', synonyms: ['country', 'pays'] },
  phone: { label: 'Business phone', group: 'business', synonyms: ['phone', 'telephone', 'téléphone', 'tel', 'phone number', 'business phone', 'main phone'] },
  website: { label: 'Website', group: 'business', synonyms: ['website', 'web', 'url', 'site', 'site web', 'web site', 'homepage'] },
  email: { label: 'Business email', group: 'business', synonyms: ['business email', 'general email', 'info email', 'courriel entreprise'] },
  pizzaFocus: { label: 'Pizza focus', group: 'business', synonyms: ['pizza focus', 'serves pizza', 'pizza'], hint: 'primary / secondary / none' },
  ownershipType: { label: 'Ownership', group: 'business', synonyms: ['ownership', 'ownership type', 'independent', 'chain', 'franchise'], hint: 'independent / group / franchise / chain' },
  locationCount: { label: 'Number of locations', group: 'business', synonyms: ['locations', 'number of locations', 'location count', 'succursales', 'branches'] },
  openedOn: { label: 'Opened on', group: 'business', synonyms: ['opened', 'opened on', 'opening date', 'date opened', 'ouverture', "date d'ouverture"], hint: 'YYYY-MM-DD' },
  description: { label: 'Notes / description', group: 'business', synonyms: ['notes', 'note', 'description', 'comments', 'commentaires', 'remarks'] },
  ownerEmail: { label: 'Salesperson (email)', group: 'assignment', synonyms: ['owner', 'owner email', 'salesperson', 'sales rep', 'rep', 'assigned to', 'représentant'], hint: 'Email of a Hearth user' },
  tags: { label: 'Tags', group: 'assignment', synonyms: ['tags', 'tag', 'labels', 'étiquettes'], hint: 'Comma-separated; must match existing tags' },
  contactFullName: { label: 'Contact full name', group: 'contact', synonyms: ['contact', 'contact name', 'full name', 'owner name', 'person', 'nom du contact'] },
  contactFirstName: { label: 'Contact first name', group: 'contact', synonyms: ['first name', 'firstname', 'given name', 'prénom', 'prenom'] },
  contactLastName: { label: 'Contact last name', group: 'contact', synonyms: ['last name', 'lastname', 'surname', 'family name', 'nom de famille'] },
  contactTitle: { label: 'Contact title', group: 'contact', synonyms: ['title', 'job title', 'position', 'role', 'titre', 'poste'] },
  contactEmail: { label: 'Contact email', group: 'contact', synonyms: ['email', 'e-mail', 'contact email', 'courriel', 'mail'] },
  contactPhone: { label: 'Contact phone', group: 'contact', synonyms: ['contact phone', 'mobile', 'cell', 'cellulaire', 'direct line'] },
};

/**
 * The values of columns that match no field, as "Header: value" lines. These are the columns a spreadsheet
 * carries that the CRM has no home for (a tracker's "current client", call dates, flags); keeping them in the
 * notes is better than dropping them. Shared so the mapping screen previews exactly what the import will keep.
 */
export function unmappedNoteLines(
  raw: Record<string, string>,
  mapping: Partial<Record<string, ImportFieldKey>>,
  headers?: readonly string[],
): string[] {
  const order = headers ?? Object.keys(raw);
  return order
    .filter((header) => !(header in mapping))
    .map((header) => ({ header, value: (raw[header] ?? '').trim() }))
    .filter(({ value }) => value !== '')
    .map(({ header, value }) => `${header}: ${value}`);
}

export const IMPORT_DUPLICATE_POLICIES = ['review', 'skip', 'merge', 'create'] as const;
export type ImportDuplicatePolicy = (typeof IMPORT_DUPLICATE_POLICIES)[number];

export const IMPORT_DUPLICATE_POLICY_LABELS: Record<ImportDuplicatePolicy, string> = {
  review: 'Merge the clear matches, let me decide the rest',
  skip: 'Skip rows that match an existing lead',
  merge: 'Fill in missing details on the existing lead',
  create: 'Create them anyway (they will be flagged for review)',
};

/**
 * POST /api/imports multipart may carry `settings` (JSON text) next to `file`: what every lead of the file gets. Omitted
 * fields keep the defaults — the uploader as salesperson when they are a rep, the "CSV import" source, the default
 * country, no email consent, clear duplicates merged and the rest left for review. The import starts right away with
 * the guessed column matching; `matchColumns` (or a file where no business-name column is recognised) stops at the
 * matching step instead.
 */
export const ImportUploadSettings = z.object({
  defaultOwnerId: z.uuid().nullable().optional(),
  defaultSourceId: z.uuid().nullable().optional(),
  defaultTagIds: z.array(z.uuid()).max(20).optional(),
  defaultCountry: z.string().trim().length(2).toUpperCase().optional(),
  duplicatePolicy: z.enum(IMPORT_DUPLICATE_POLICIES).optional(),
  defaultConsentBasis: z.enum(IMPORT_CONSENT_BASES).optional(),
  /** What the file's businesses are: the leads it creates go to that list. Merges keep the existing lead's kind. */
  businessKind: z.enum(BUSINESS_KINDS).optional(),
  matchColumns: z.boolean().optional(),
});
export type ImportUploadSettings = z.infer<typeof ImportUploadSettings>;

/** PATCH /api/imports/:id — mapping and defaults, while the import waits for its columns to be matched. */
export const UpdateImportBody = z.object({
  columnMapping: z.record(z.string(), z.enum(IMPORT_FIELD_KEYS)),
  defaultOwnerId: z.uuid().nullable(),
  defaultSourceId: z.uuid().nullable(),
  defaultTagIds: z.array(z.uuid()).max(20),
  defaultCountry: z.string().trim().length(2).toUpperCase(),
  duplicatePolicy: z.enum(IMPORT_DUPLICATE_POLICIES),
  /** Keep the values of columns that match no field, appended to the lead's notes. Omit to leave it as it is. */
  appendUnmappedToNotes: z.boolean().optional(),
  /** Where the file's email addresses came from — recorded as the consent basis of the contacts it creates. Omit to keep. */
  defaultConsentBasis: z.enum(IMPORT_CONSENT_BASES).optional(),
  /** What the file's businesses are (pizzerias, dealers or reps). Omit to keep. */
  businessKind: z.enum(BUSINESS_KINDS).optional(),
});
export type UpdateImportBody = z.infer<typeof UpdateImportBody>;

export const ImportRowsQuery = z.object({
  status: z.enum(IMPORT_ROW_STATUSES).optional(),
  unresolved: z.preprocess((v) => v === 'true' || v === true, z.boolean()).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type ImportRowsQuery = z.input<typeof ImportRowsQuery>;

/** GET /api/imports — `batchId` lists every import of one folder scan (no 50-item cap then). */
export const ImportListQuery = z.object({ batchId: z.uuid().optional() });
export type ImportListQuery = z.infer<typeof ImportListQuery>;

/**
 * POST /api/imports/probe — multipart `file`, same limits as an upload. Reads the file and runs the column guess and
 * the quality scan on every row, then forgets it: nothing is stored and no lead is touched. Used by the folder scan to
 * rank files by how many real leads they hold. An unreadable file answers 422 with the same message an upload gets.
 */
export type ImportProbeDTO = {
  fileType: ImportFileType;
  parseNotes: string[];
  /** Rows read (at most MAX_IMPORT_ROWS); 0 for a file with a header row and no data rows. */
  totalRows: number;
  /** More than MAX_IMPORT_ROWS rows: the file must be split before it can be imported. */
  truncated: boolean;
  /** CRM fields the columns were matched to (empty when not even a business name column was found). */
  mappedFields: ImportFieldKey[];
  /** A business-name column was found, so the file can be imported without matching columns by hand. */
  hasNameColumn: boolean;
  /**
   * Quality scan over every row (same rules as validation; duplicates are not checked). The four add up to totalRows:
   * a row without a business name or with a mapping error is `invalid` — so every row is, when hasNameColumn is false.
   */
  leads: { good: number; weak: number; junk: number; invalid: number };
  /** A few business names from the best rows, to recognise the file. */
  sampleNames: string[];
};

/** POST /api/imports multipart may carry a `batchId` field (uuid) next to `file` to group a folder scan's uploads. */
export const ImportBatchField = z.uuid();

/**
 * Decide what happens to possible duplicates still waiting (specific rows, or every undecided one). The decision is
 * applied right away: the import picks the rows up again ('importing') and creates, merges or skips them.
 */
export const ResolveImportRowsBody = z.object({
  rowIds: z.array(z.uuid()).max(MAX_IMPORT_ROWS).optional(),
  allUnresolved: z.boolean().optional(),
  resolution: z.enum(DUPLICATE_RESOLUTIONS),
});
export type ResolveImportRowsBody = z.infer<typeof ResolveImportRowsBody>;

/**
 * Import scan score bands (0–100): below `weak` a row is junk and is not imported unless someone includes it;
 * `good` and up is a lead worth a salesperson's time. Hard problems (placeholder name, header or total row,
 * no way to reach it, not a food business, closed) make a row junk whatever its score.
 */
export const LEAD_QUALITY_THRESHOLDS = { weak: 30, good: 60 } as const;
export type LeadQualityVerdict = 'good' | 'weak' | 'junk';
export function leadQualityVerdict(score: number): LeadQualityVerdict {
  return score >= LEAD_QUALITY_THRESHOLDS.good ? 'good' : score >= LEAD_QUALITY_THRESHOLDS.weak ? 'weak' : 'junk';
}

/**
 * Overrule the quality scan: import junk rows anyway (specific rows or all of them) — they go in right away, or join the
 * possible duplicates when they repeat a lead — or set possible duplicates still waiting aside as junk.
 */
export const ImportRowQualityBody = z.object({
  rowIds: z.array(z.uuid()).max(MAX_IMPORT_ROWS).optional(),
  allJunk: z.boolean().optional(),
  action: z.enum(['include', 'exclude']),
});
export type ImportRowQualityBody = z.infer<typeof ImportRowQualityBody>;

export type ImportJobDTO = {
  id: string;
  filename: string;
  fileType: ImportFileType;
  /** Size of the uploaded file in bytes; null for imports staged before files were kept. */
  fileSize: number | null;
  /** How the file was read: sheet used, text encoding, leads found in free text… */
  parseNotes: string[];
  /** Set when the file came from a folder scan together with others (see GET /api/imports?batchId=). */
  batchId: string | null;
  status: ImportStatus;
  headers: string[];
  columnMapping: Partial<Record<string, ImportFieldKey>>;
  /** Mapping guessed from the header names; shown until the user saves their own. */
  suggestedMapping: Partial<Record<string, ImportFieldKey>>;
  /** The sampled rows as uploaded (the first 25): examples on the mapping screen and the preview of the notes they keep. */
  sample: Record<string, string>[];
  defaultOwnerId: string | null;
  defaultSourceId: string | null;
  defaultTagIds: string[];
  defaultCountry: string;
  duplicatePolicy: ImportDuplicatePolicy;
  defaultConsentBasis: ImportConsentBasis;
  /** The list the leads this import creates go to. */
  businessKind: BusinessKind;
  appendUnmappedToNotes: boolean;
  /**
   * Columns that hold a value in the sampled rows, with how many rows do and an example. The mapping screen lists the
   * ones its current matching leaves without a field, so a column set to "Don’t import" never disappears quietly.
   */
  filledColumns: { header: string; filledRows: number; sample: string }[];
  /**
   * For each column, the fields its sampled values mostly don't fit, with why ("4 of 5 values aren’t phone numbers —
   * e.g. “call Tuesday”"). Only filled while the job is `uploaded` (the mapping step), `{}` otherwise.
   */
  columnIssues: Record<string, Partial<Record<ImportFieldKey, string>>>;
  totalRows: number;
  /** Rows checked and waiting to be imported; 0 once the import has run (every ready row is imported straight away). */
  validRows: number;
  /** Rows that cannot become a lead (no business name): listed with their problem and in the report. */
  invalidRows: number;
  /** Possible duplicates not imported yet: waiting for a decision (see unresolvedDuplicates) or for the row they repeat. */
  duplicateRows: number;
  /** Rows the quality scan judged not to be usable leads; they are not imported unless someone includes them. */
  junkRows: number;
  /** Possible duplicates waiting for someone to decide create / merge / skip: the import "needs review" while any are. */
  unresolvedDuplicates: number;
  createdCount: number;
  mergedCount: number;
  skippedCount: number;
  failedCount: number;
  error: string | null;
  createdBy: { id: string; name: string } | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
};

export type ImportRowDTO = {
  id: string;
  rowNumber: number;
  raw: Record<string, string>;
  /** What the row becomes: business name, city, contact… (after mapping and normalization). */
  preview: { name: string | null; city: string | null; phone: string | null; website: string | null; contact: string | null };
  status: ImportRowStatus;
  errors: string[];
  warnings: string[];
  /** Lead quality 0–100 from the import scan (null until validated), with what raised or lowered it. */
  qualityScore: number | null;
  qualityReasons: string[];
  duplicate: { businessId: string; name: string; city: string | null; confidence: number; reasons: string[] } | null;
  duplicateOfRow: number | null;
  /** Why this row looks like `duplicateOfRow` (null when it also matches a CRM lead: `duplicate` explains that one). */
  duplicateOfRowMatch: { confidence: number; reasons: string[] } | null;
  resolution: DuplicateResolution | null;
  businessId: string | null;
};
